import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicTable, { SelectColumnFilter } from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import { DeleteButton, EditButton } from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import {
  PlanningTask,
  PlanningTaskResponse,
  deletePlanningTask,
  getPlanningTasksByProjectId,
} from '@/services/PlanningAOTasksService';
import { useTranslation } from 'react-i18next';
import {
  EventType,
  PlanningTaskTimeLine,
  WeekendsType,
} from './PlanningTaskTimeLine';

function PlanningAOTasks() {
  const { id } = useParams();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [weekendDays, setWeekendDays] = useState<WeekendsType[]>([]);
  const [allWeekendDays, setAllWeekendDays] = useState<WeekendsType[]>([]);
  const navigate = useNavigate();

  const { t } = useTranslation();

  const ActionButtonsCell = ({ value }: any) => {
    return (
      <div>
        <DeleteButton
          onClick={() => {
            setSelectedRow(value);
            setIsModalVisible(true);
          }}
        >
          {t('PlanningAOTasks.deleteButton')}
        </DeleteButton>

        <EditButton
          type="button"
          onClick={() => {
            navigate(`${value}`);
          }}
        >
          {t('PlanningAOTasks.editButton')}
        </EditButton>
      </div>
    );
  };

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeletePlanningTask.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('PlanningAOTasks.taskNameColumn'),
        accessor: 'taskName',
        Filter: SelectColumnFilter,
      },
      {
        Header: t('PlanningAOTasks.startDateColumn'),
        accessor: 'startDate',
      },
      {
        Header: t('PlanningAOTasks.endDateColumn'),
        accessor: 'endDate',
      },
      {
        Header: t('PlanningAOTasks.durationColumn'),
        accessor: 'duration',
      },
      {
        Header: t('PlanningAOTasks.progressColumn'),
        accessor: 'progress',
      },
      {
        Header: t('PlanningAOTasks.responsibleColumn'),
        accessor: 'responsibleName',
      },
      {
        Header: t('PlanningAOTasks.actionsColumn'),
        accessor: 'id',
        Cell: ({ cell: { value } }: any) => (
          <ActionButtonsCell
            value={value}
            handleDelete={handleDeletePlanningTask}
          />
        ),
      },
    ],
    [t]
  );

  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    data: planningTasks,
  } = useQuery<PlanningTaskResponse | undefined>(
    ['planningTasks', pageIndex, pageSize, search, id],
    () =>
      getPlanningTasksByProjectId({
        projectId: id!,
        page: pageIndex,
        take: pageSize,
        search: search,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
      enabled: id !== null,
    }
  );

  const handleDeletePlanningTask = useMutation(
    async (id: string) => {
      await deletePlanningTask(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'planningTasks',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((planningTasks?.count ?? 0) / pageSize);
  }, [pageSize, planningTasks]);

  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  const convertPlanningTasksToEvent = (
    planingTasks: PlanningTask[] | undefined
  ): EventType[] => {
    return (
      planingTasks?.map((planingTask) => ({
        startDate: planingTask.startDate,
        endDate: planingTask.endDate,
        taskName: planingTask.taskName!,
        resourceId: planingTask.id!,
        tooltip: planingTask.taskName,
      })) ?? []
    );
  };
  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={t('PlanningAOTasks.pageName')}
        addbtn={true}
        addRouter="new"
      />
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <PlanningTaskTimeLine
            resourcesLength={planningTasks?.count ?? 0}
            resourcesList={planningTasks?.planningTasks ?? []}
            searchResource={search}
            myEvents={convertPlanningTasksToEvent(planningTasks?.planningTasks)}
            setSearchResource={setSearch}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            handleEditClick={(id) => {
              navigate(`${id}`);
            }}
            handleDeleteClick={(id) => {
              setSelectedRow(id);
              setIsModalVisible(true);
            }}
          />

          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}

          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </DefaultLayout>
  );
}

export default PlanningAOTasks;
